import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LoaderCircle, Search, X } from 'lucide-react';
import { checkLogin, setOtpVerified } from '../../Common/store';
import { getGasystemCustomerBalance, searchGasystemCustomers } from '../api/gasystemCustomers';
import MappedManualPayment from './MappedManualPayment';

const SEARCH_LIMIT = 100;

function normalizeCustomer(row = {}) {
  const selectionToken = String(row.selectionToken ?? row.selection_token ?? '');
  const rawPayable = row.payable ?? row.isPayable ?? row.is_payable;
  const payable = rawPayable === undefined || rawPayable === null
    ? Boolean(selectionToken)
    : rawPayable === true || rawPayable === 1 || ['1', 'TRUE', 'Y', 'YES'].includes(String(rawPayable).trim().toUpperCase());
  return {
    drugCode: String(row.drugCode ?? row.drug_code ?? ''),
    drugName: String(row.drugName ?? row.drug_name ?? ''),
    businessNo: String(row.businessNo ?? row.business_no ?? ''),
    ownerName: String(row.ownerName ?? row.owner_name ?? ''),
    phone: String(row.phone ?? ''),
    drugSaleCode: String(row.drugSaleCode ?? row.drug_sale_code ?? ''),
    drugSaleName: String(row.drugSaleName ?? row.drug_sale_name ?? ''),
    payable,
    unavailableReason: String(row.unavailableReason ?? row.unavailable_reason ?? ''),
    selectionToken,
  };
}

function formatBusinessNo(value) {
  const original = String(value || '');
  const digits = original.replace(/\D/g, '');
  if (digits.length !== 10) return original || '-';
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function getErrorCode(error) {
  return Number(error?.code ?? error?.response?.data?.code ?? 0);
}

function getErrorMessage(error, fallback) {
  return error?.message || error?.response?.data?.msg || fallback;
}

export default function GasystemExternalManualPayment({
  config,
  customerSearch = searchGasystemCustomers,
  submitPayment,
  loginPath = '/app/login',
  delegatedPayment = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searched, setSearched] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [balanceResult, setBalanceResult] = useState(null);
  const [balanceRetry, setBalanceRetry] = useState(0);
  const [notice, setNotice] = useState('');

  const logoutForExpiredSession = useCallback(() => {
    dispatch(setOtpVerified(false));
    dispatch(checkLogin(false));
    navigate(loginPath, { replace: true });
  }, [dispatch, loginPath, navigate]);

  const showNotice = useCallback((message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  }, []);

  useEffect(() => {
    if (delegatedPayment || !selected?.selectionToken) {
      setBalanceResult(null);
      return undefined;
    }
    const controller = new AbortController();
    const selectionToken = selected.selectionToken;
    setBalanceResult({ selectionToken, status: 'loading' });
    getGasystemCustomerBalance(selectionToken, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setBalanceResult({ selectionToken, status: 'success', data });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (getErrorCode(error) === 9999) logoutForExpiredSession();
        else setBalanceResult({ selectionToken, status: 'error' });
      });
    return () => controller.abort();
  }, [delegatedPayment, selected?.selectionToken, logoutForExpiredSession, balanceRetry]);

  const search = async (nextPage = 1) => {
    if (searching) return;
    const keyword = searchKeyword.trim();
    const normalizedBusinessNo = keyword.replace(/\D/g, '');
    const isBusinessNoSearch = Boolean(normalizedBusinessNo) && /^[\d\s-]+$/.test(keyword);
    setSearching(true);
    setResultsOpen(true);
    setSelected(null);
    setNotice('');
    try {
      const data = await customerSearch({
        q: isBusinessNoSearch ? '' : keyword,
        businessNo: isBusinessNoSearch ? normalizedBusinessNo : '',
        page: nextPage,
        limit: SEARCH_LIMIT,
      }, config?.externalCustomerSearchEndpoint);
      setResults(Array.isArray(data?.rows) ? data.rows.map(normalizeCustomer) : []);
      setPage(Number(data?.page || nextPage));
      setTotalPages(Math.max(0, Number(data?.totalPages ?? data?.total_pages ?? 0)));
      setSearched(true);
    } catch (error) {
      const code = getErrorCode(error);
      setResults([]);
      setTotalPages(0);
      setSearched(true);
      setResultsOpen(false);
      if (code === 9999) {
        logoutForExpiredSession();
      } else if (code === 5020) {
        showNotice('가시스템 외부 거래처 조회에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      } else if (code === 4040) {
        showNotice('담당자 또는 가맹점 상태를 확인할 수 없습니다.');
      } else {
        showNotice(getErrorMessage(error, '거래처 검색 중 오류가 발생했습니다.'));
      }
    } finally {
      setSearching(false);
    }
  };

  const chooseCustomer = (customer) => {
    if (!customer.payable || !customer.selectionToken) {
      showNotice(customer.unavailableReason || '담당 영업자를 확인할 수 없어 결제할 수 없습니다.');
      return;
    }
    setSelected(customer);
    setResultsOpen(false);
    setNotice('');
  };

  const clearSelection = () => {
    setSelected(null);
    setResultsOpen(searched);
  };
  const invalidateSelection = () => {
    setSelected(null);
    setResultsOpen(false);
  };
  const resetAfterPayment = () => {
    setSelected(null);
    setSearchKeyword('');
    setResults([]);
    setPage(1);
    setTotalPages(0);
    setSearched(false);
    setResultsOpen(false);
  };
  const hasUnselectedInput = !selected && Boolean(searchKeyword.trim());
  const resultsVisible = !notice && !selected && resultsOpen && (searching || searched);

  useEffect(() => {
    if (!resultsVisible || typeof window === 'undefined' || window.matchMedia('(min-width: 640px)').matches) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [resultsVisible]);

  const renderResultRows = (mobile = false) => !searching && results.map((customer, index) => (
    <button
      key={`${customer.drugCode}-${customer.businessNo}-${index}`}
      type="button"
      onClick={() => chooseCustomer(customer)}
      disabled={!customer.payable || !customer.selectionToken}
      aria-disabled={!customer.payable || !customer.selectionToken}
      style={mobile
        ? { ...styles.resultRow, ...styles.mobileResultRow, ...(!customer.payable || !customer.selectionToken ? styles.disabledResultRow : {}) }
        : { ...styles.resultRow, ...(!customer.payable || !customer.selectionToken ? styles.disabledResultRow : {}) }}
    >
      <span style={styles.resultTitleLine}>
        {customer.drugCode && (
          <span style={mobile ? { ...styles.resultCode, ...styles.mobileResultCode } : styles.resultCode}>
            {customer.drugCode} ·
          </span>
        )}
        <strong style={mobile ? { ...styles.resultName, ...styles.mobileResultName } : styles.resultName}>{customer.drugName || '-'}</strong>
        <span style={mobile ? { ...styles.resultBusinessNo, ...styles.mobileResultBusinessNo } : styles.resultBusinessNo}>· {formatBusinessNo(customer.businessNo)}</span>
      </span>
      <span style={!customer.payable || !customer.selectionToken
        ? { ...(mobile ? { ...styles.resultMeta, ...styles.mobileResultMeta } : styles.resultMeta), ...styles.unavailableReason }
        : (mobile ? { ...styles.resultMeta, ...styles.mobileResultMeta } : styles.resultMeta)}>
        {!customer.payable || !customer.selectionToken
          ? (customer.unavailableReason || '결제할 수 없는 거래처입니다.')
          : [
              customer.ownerName,
              customer.phone,
              delegatedPayment && (customer.drugSaleName || customer.drugSaleCode)
                ? `담당 ${customer.drugSaleName || '-'}${customer.drugSaleCode ? ` · ${customer.drugSaleCode}` : ''}`
                : '',
            ].filter(Boolean).join(' · ') || '-'}
      </span>
    </button>
  ));

  const renderPagination = (mobile = false) => totalPages > 1 && !searching && (
    <div style={mobile ? { ...styles.pagination, ...styles.mobilePagination } : styles.pagination}>
      <button type="button" disabled={page <= 1} onClick={() => search(page - 1)} style={styles.pageButton} aria-label="이전 페이지"><ChevronLeft size={16} /></button>
      <strong style={styles.pageText}>{page} / {totalPages}</strong>
      <button type="button" disabled={page >= totalPages} onClick={() => search(page + 1)} style={styles.pageButton} aria-label="다음 페이지"><ChevronRight size={16} /></button>
    </div>
  );

  const customerSearchField = (
    <div style={styles.fieldWrap}>
      <input
        value={selected ? selected.drugName : searchKeyword}
        onChange={(event) => setSearchKeyword(event.target.value)}
        onKeyDown={(event) => { if (!selected && event.key === 'Enter') search(1); }}
        onFocus={() => { if (!selected && searched) setResultsOpen(true); }}
        readOnly={Boolean(selected)}
        placeholder="거래처명 또는 사업자번호"
        aria-label="거래처명 또는 사업자번호"
        aria-describedby={hasUnselectedInput ? 'gasystem-customer-selection-guide' : undefined}
        autoComplete="off"
        style={{ ...styles.searchInput, ...(selected ? styles.selectedInput : {}) }}
      />
      <button
        type="button"
        onClick={() => selected ? clearSelection() : search(1)}
        disabled={searching}
        aria-label={selected ? '거래처 변경' : '거래처 검색'}
        title={selected ? '거래처 변경' : '거래처 검색'}
        style={styles.searchIconButton}
      >
        {selected ? <X size={18} /> : <Search size={18} />}
      </button>

      {hasUnselectedInput && (
        <small id="gasystem-customer-selection-guide" role="status" style={styles.selectionGuide}>
          검색 결과에서 거래처를 선택해 주세요.
        </small>
      )}

      {!delegatedPayment && selected && balanceResult?.selectionToken === selected.selectionToken && (
        <div role="status" style={styles.balanceStatus}>
          {balanceResult.status === 'loading' ? '잔액 조회 중...' : (
            balanceResult.status === 'success' && Number.isSafeInteger(balanceResult.data?.balance) ? (
              <span>잔액 <strong style={styles.balanceAmount}>{balanceResult.data.balance.toLocaleString('ko-KR')}원</strong></span>
            ) : (
              <>
                <span>잔액을 확인할 수 없습니다.</span>
                <button type="button" style={styles.balanceRetry} onClick={() => setBalanceRetry((value) => value + 1)}>다시 조회</button>
              </>
            )
          )}
        </div>
      )}

      {notice && <div role="alert" style={{ ...styles.notice, top: hasUnselectedInput ? 58 : 40 }}>{notice}</div>}

      {resultsVisible && (
        <div className="hidden sm:block" style={{ ...styles.dropdown, top: hasUnselectedInput ? 58 : 40 }}>
          <div style={styles.dropdownStatus}>
            <span style={styles.dropdownStatusLabel}>
              {searching && <LoaderCircle size={14} className="animate-spin" />}
              {searching ? '검색 중' : '검색 결과'}
            </span>
            <span style={styles.dropdownStatusCount}>
              {searching ? '잠시만 기다려 주세요' : results.length ? `${results.length.toLocaleString('ko-KR')}건` : '결과 없음'}
            </span>
            {!searching && (
              <button type="button" onClick={() => setResultsOpen(false)} aria-label="검색 결과 닫기" style={styles.dropdownClose}>
                <X size={14} />
              </button>
            )}
          </div>
          {renderResultRows()}
          {renderPagination()}
        </div>
      )}
    </div>
  );

  const mobileResultsSheet = resultsVisible && typeof document !== 'undefined'
    ? createPortal(
      <div className="gasystem-customer-sheet-backdrop fixed inset-0 z-[4200] sm:hidden">
        <button
          type="button"
          className="absolute inset-0 h-full w-full border-0 bg-black/45"
          onClick={() => setResultsOpen(false)}
          aria-label="검색 결과 닫기"
        />
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="gasystem-customer-sheet-title"
          className="gasystem-customer-sheet absolute inset-x-0 bottom-0 flex max-h-[72dvh] min-h-[280px] flex-col overflow-hidden rounded-t-[22px] bg-white shadow-2xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex h-7 shrink-0 items-center justify-center" aria-hidden="true">
            <span className="h-1 w-10 rounded-full bg-[#d9dee5]" />
          </div>
          <header className="flex shrink-0 items-center gap-3 border-b border-[#eef1f4] px-5 pb-4 pt-1">
            <div className="min-w-0 flex-1">
              <h2 id="gasystem-customer-sheet-title" className="text-[17px] font-extrabold text-[#191f28]">거래처 검색 결과</h2>
              <p className="mt-0.5 text-[11px] text-[#8b95a1]">
                {searching ? '거래처를 검색하고 있습니다.' : results.length ? `${results.length.toLocaleString('ko-KR')}건이 조회되었습니다.` : '검색 결과가 없습니다.'}
              </p>
            </div>
            <button type="button" onClick={() => setResultsOpen(false)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f2f4f6] text-[#4e5968]" aria-label="닫기">
              <X size={18} />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {searching ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-[#6b7684]">
                <LoaderCircle size={30} className="animate-spin text-[var(--brand-primary)]" />
                <span className="text-[13px] font-semibold">검색 중...</span>
              </div>
            ) : results.length ? renderResultRows(true) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center px-6 text-center text-[#8b95a1]">
                <Search size={28} />
                <p className="mt-3 text-[13px] font-semibold">검색 결과가 없습니다.</p>
              </div>
            )}
          </div>
          {renderPagination(true)}
        </section>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <MappedManualPayment
        config={config}
        variant="B"
        externalCustomerMode
        externalCustomer={selected}
        externalCustomerField={customerSearchField}
        onExternalSelectionInvalid={invalidateSelection}
        onExternalPaymentCompleted={resetAfterPayment}
        submitPayment={submitPayment}
        loginPath={loginPath}
        submitButtonLabel={delegatedPayment ? '대리 결제하기' : '결제하기'}
        salespersonLabel={delegatedPayment ? '담당 영업자' : '영업자명'}
        defaultProductName="상품결제"
        topContent={delegatedPayment ? (
          <div style={styles.delegatedNotice}>
            <strong style={styles.delegatedNoticeTitle}>CP 대리 결제</strong>
            <span>거래처를 선택하면 담당 영업자가 자동 지정되며, 결제 실적은 해당 영업자에게 귀속됩니다.</span>
          </div>
        ) : null}
      />
      {mobileResultsSheet}
    </>
  );
}

const styles = {
  delegatedNotice: { margin: '12px 20px 0', padding: '11px 13px', border: '1px solid #b9d7ff', borderRadius: 10, background: '#f1f7ff', color: '#33506f', fontSize: 11, lineHeight: 1.5, display: 'flex', flexDirection: 'column', gap: 2 },
  delegatedNoticeTitle: { color: 'var(--brand-primary)', fontSize: 12, fontWeight: 800 },
  fieldWrap: { position: 'relative', width: '100%', minWidth: 0 },
  balanceStatus: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, color: '#6b7684', fontSize: 12 },
  balanceAmount: { color: '#191f28', fontWeight: 700 },
  balanceRetry: { padding: 0, border: 0, background: 'none', color: 'var(--brand-primary)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' },
  searchInput: { width: '100%', height: 35, minWidth: 0, boxSizing: 'border-box', padding: '2px 32px 2px 4px', border: 0, borderBottom: '1px solid #f0f0f0', borderRadius: 0, background: 'transparent', color: '#191f28', outline: 'none', fontSize: 20, fontWeight: 600, lineHeight: '30px' },
  selectedInput: { color: '#4e5968' },
  searchIconButton: { position: 'absolute', top: 1, right: 0, width: 32, height: 32, border: 0, background: 'transparent', color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  selectionGuide: { display: 'block', marginTop: 5, color: '#c25b12', fontSize: 10, fontWeight: 650, lineHeight: 1.4 },
  notice: { position: 'absolute', zIndex: 90, top: 40, left: 0, width: 'min(360px, calc(100vw - 52px))', boxSizing: 'border-box', border: '1px solid #ffc9c9', borderRadius: 9, padding: '10px 12px', background: '#fff1f1', color: '#c53030', fontSize: 12, fontWeight: 700, lineHeight: 1.45, boxShadow: '0 10px 24px rgba(15,23,42,.14)' },
  dropdown: { position: 'absolute', zIndex: 80, top: 40, left: 0, width: 'min(360px, calc(100vw - 52px))', maxHeight: 246, overflowY: 'auto', overscrollBehavior: 'contain', boxSizing: 'border-box', border: '1px solid #e5e8eb', borderRadius: 10, background: '#fff', boxShadow: '0 8px 20px rgba(15,23,42,.11)' },
  dropdownStatus: { position: 'sticky', top: 0, zIndex: 1, minHeight: 36, display: 'flex', alignItems: 'center', gap: 7, boxSizing: 'border-box', padding: '8px 9px 8px 12px', borderBottom: '1px solid #eef1f4', background: '#f8fafc', color: '#6b7684', fontSize: 11 },
  dropdownStatusLabel: { display: 'inline-flex', alignItems: 'center', gap: 5, color: '#333d4b', fontWeight: 750 },
  dropdownStatusCount: { marginLeft: 'auto', color: '#8b95a1', fontSize: 10 },
  dropdownClose: { width: 24, height: 24, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 0, borderRadius: 6, background: 'transparent', color: '#8b95a1', cursor: 'pointer' },
  resultRow: { width: '100%', minHeight: 56, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, boxSizing: 'border-box', padding: '9px 12px', border: 0, borderBottom: '1px solid #f0f2f4', background: '#fff', color: '#191f28', textAlign: 'left', cursor: 'pointer' },
  disabledResultRow: { background: '#f8f9fa', color: '#8b95a1', cursor: 'not-allowed', opacity: 0.78 },
  resultTitleLine: { display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0, whiteSpace: 'nowrap' },
  resultCode: { flexShrink: 0, color: 'var(--brand-primary)', fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  resultName: { fontSize: 12, fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' },
  resultBusinessNo: { flexShrink: 0, color: '#8b95a1', fontSize: 10, fontWeight: 550 },
  resultMeta: { color: '#6b7684', fontSize: 10, lineHeight: 1.4 },
  unavailableReason: { color: '#c2410c', fontWeight: 650 },
  mobileResultRow: { minHeight: 68, gap: 5, padding: '12px 20px' },
  mobileResultCode: { fontSize: 13, fontWeight: 800 },
  mobileResultName: { fontSize: 14, fontWeight: 800 },
  mobileResultBusinessNo: { fontSize: 11 },
  mobileResultMeta: { fontSize: 11 },
  pagination: { position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 8, borderTop: '1px solid #eef1f4', background: '#fff' },
  mobilePagination: { position: 'relative', flexShrink: 0, padding: '11px 8px' },
  pageButton: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dfe3e8', borderRadius: 8, background: '#fff' },
  pageText: { color: '#4e5968', fontSize: 11 },
};
